import { API, graphqlOperation } from "aws-amplify";
import dayjs from "dayjs";
import * as queries from "../graphql/queries";
import * as mutations from "../graphql/mutations";
import {
  getUserInfo,
  calcLineColorFromIntervalAndModelName,
  errorToString,
  EXCHANGE_INSTRUMENT,
  retrieveIntervalInMinFromModelName,
  retrieveIntervalFromModelName,
  UNGROUPED_NAME,
  determineTradeTypeByExchangeInstrument,
  INSTRUMENT,
  getExchangeIdFromExchangeInstrument,
} from "../utils";
import { GSI_PK, TOUCHSTONE_TASKS_ACTIVE_STATUS } from "../utils/touchstone";
import { message } from "antd";
import { defaults } from "lodash";
import { encrypt, importRsaKey } from "./CryptographyService";

export * from "./ElAdminEventLogService";

function generateModelIdFromModelNames(modelName1, modelName2, symbol) {
  const time1 = modelName1.split(".")[0];
  const time2 = modelName2.split(".")[0];
  const interval = retrieveIntervalFromModelName(modelName1);
  return `${time2}.${time1}.${symbol}.${interval}`;
}

function calcFetchChunkSize(rangeStart, rangeEnd, parallelize) {
  const maxChunks = parallelize ? 20 : 1;
  const minChunkDuration = 12 * 3600;
  return Math.max(minChunkDuration, Math.ceil((rangeEnd - rangeStart) / maxChunks));
}

export default class Service {
  static async getCognitoAdminPermission(userEmail) {
    const {
      data: { getCognitoAdminPermission: adminPermissions },
    } = await API.graphql(
      graphqlOperation(queries.getCognitoAdminPermission, {
        email: userEmail,
      })
    );
    return adminPermissions;
  }

  static async listBalances(timestamp_15mPointNumStart, timestamp_15mPointNumEnd) {
    const userInfo = await getUserInfo();
    const adminPermissions = await Service.getCognitoAdminPermission(userInfo.email);

    console.log("adminPermissions", adminPermissions);

    if (!adminPermissions) {
      alert("you don't have any permissions!");
      return;
    }

    const { managedAccounts } = adminPermissions;

    let accountBalances = [];

    let fetchPromises = [];
    managedAccounts.forEach((account) => {
      for (let exchangeInstrument in EXCHANGE_INSTRUMENT) {
        let exchangeId = account.name.split("_")[0]; // binance_yichen0830 -> binance
        if (exchangeInstrument.includes(exchangeId)) {
          // binance_yichen0830 -> binance_coin_future_yichen0830
          let exchangeAccount = account.name.replace(exchangeId, exchangeInstrument);
          fetchPromises.push(fetchAccountBalances(exchangeAccount, timestamp_15mPointNumStart * 1000, timestamp_15mPointNumEnd * 1000));
        }
      }
    });
    const balanceBatches = await Promise.all(fetchPromises);
    balanceBatches.forEach((batch) => (accountBalances = accountBalances.concat(batch)));

    const sortedAccountBalances = accountBalances.sort((a, b) => b.timestamp - a.timestamp);

    /**
     * 处理fetch的数据用在table里
     */
    let balanceTimeColumnSet = new Set();
    let balanceDataSource = [];
    let balanceTimeColumns = [];

    let aggregBalances = {};
    let parentBalances = {};

    /**
     * 把account在不同时间的balance aggregate到一个record里
     */
    for (let i = 0; i < sortedAccountBalances.length; i++) {
      let processedBalances = {};
      const balance = sortedAccountBalances[i];
      let parsedBalance = JSON.parse(balance.balances);

      /**
       * parse time string
       **/
      const date = new Date(Number(balance.timestamp));
      const timeArr = date.toLocaleTimeString().split(":");
      const ampm = timeArr[2].split(" ")[1];
      const timeStr = `${date.toLocaleDateString()} ${timeArr[0]}:${timeArr[1]} ${ampm}`;

      balanceTimeColumnSet.add(timeStr);

      if (!aggregBalances[balance.exchangeAccount]) {
        aggregBalances[balance.exchangeAccount] = {
          key: balance.exchangeAccount,
          account: balance.exchangeAccount,
          parentAccount: `${balance.exchangeAccount.split("_")[0]}_${balance.parentAccount}`,
        };
      }
      // 每个balance只保留2位小数
      for (let symbol of Object.keys(parsedBalance)) {
        if (parsedBalance[symbol].total > 0) {
          const balanceWithTwoDecimal = Number(parsedBalance[symbol].total.toFixed(2));
          processedBalances[symbol] = balanceWithTwoDecimal;
        }
      }
      aggregBalances[balance.exchangeAccount][timeStr] = JSON.stringify(processedBalances);
    }

    /**
     * 把parentAccount在不同时间的balance aggregate到一个record里
     */
    for (let aggregBalance of Object.values(aggregBalances)) {
      const { parentAccount } = aggregBalance;
      const isNewParentAccount = !Object.keys(parentBalances).includes(parentAccount);
      if (isNewParentAccount) {
        parentBalances[parentAccount] = {
          key: parentAccount,
          account: parentAccount,
        };
      }

      const parentBalance = parentBalances[parentAccount];
      for (let timeStr of Array.from(balanceTimeColumnSet.keys())) {
        if (!aggregBalance[timeStr]) {
          continue;
        }
        const parsedBalance = JSON.parse(aggregBalance[timeStr]);
        if (!parentBalance[timeStr]) {
          parentBalance[timeStr] = "{}";
        }

        const parentBalanceAtThisTime = JSON.parse(parentBalance[timeStr]);
        for (let symbol of Object.keys(parsedBalance)) {
          const balanceWithTwoDecimal = Number(parsedBalance[symbol].toFixed(2));
          parentBalanceAtThisTime[symbol] = (Number(parentBalanceAtThisTime[symbol] || 0) + balanceWithTwoDecimal).toFixed(2);
        }
        parentBalance[timeStr] = JSON.stringify(parentBalanceAtThisTime);
      }
    }

    balanceDataSource = Object.values({ ...aggregBalances, ...parentBalances });

    balanceDataSource = balanceDataSource.sort((a, b) => {
      if (!a.parentAccount) return -1; // parentAccount will be at the bottom
      if (!b.parentAccount) return 1;
      return a.key.localeCompare(b.key);
    });
    balanceTimeColumns = Array.from(balanceTimeColumnSet.keys()).map((timeStr) => ({
      title: timeStr,
      dataIndex: timeStr,
      key: timeStr,
    }));
    return { balanceDataSource, balanceTimeColumns };
  }

  static async fetchBacktestKPIs(symbol, start, end, intervalIn15Mins, averageOnly) {
    let nextToken = undefined;
    let kpis = [];

    do {
      const options = {
        symbol: `${symbol}_${intervalIn15Mins}_15m`,
        timestamp_15mPointNum: {
          between: [start, end],
        },
        nextToken,
      };
      options.limit = 1000;
      if (averageOnly) {
        options.filter = { modelName1: { eq: "averaged" } };
      } else {
        options.filter = { modelName1: { ne: "averaged" } };
      }
      const {
        data: { listModelBacktestKPIS: kpiBatch },
      } = await API.graphql(graphqlOperation(queries.listModelBacktestKPIS, options));
      kpis = kpis.concat(kpiBatch.items);
      nextToken = kpiBatch.nextToken;
    } while (nextToken);

    return kpis;
  }

  static async listBacktestKPIs(
    symbol,
    pnlVisibility,
    timestamp_15mPointNumStart,
    timestamp_15mPointNumEnd,
    parallelize,
    intervalIn15Mins,
    averageOnly = false,
    cumulativeReturnCol = "cumulativeReturn1_5BPS" // cumulativeReturn1_5BPS, cumulativeReturn2BPS, cumulativeReturn3BPS...
  ) {
    const chunkSize = calcFetchChunkSize(timestamp_15mPointNumStart, timestamp_15mPointNumEnd, parallelize);
    const kpis = await Service.fetchParallel(
      (start, end) => Service.fetchBacktestKPIs(symbol, start, end, intervalIn15Mins, averageOnly),
      timestamp_15mPointNumStart,
      timestamp_15mPointNumEnd,
      chunkSize
    );

    const eonPredictorPredictions = await Service.fetchParallel(
      (start, end) => Service.fetchModelPredictions(`eon_model_predictor_${symbol}_specific`, start, end),
      timestamp_15mPointNumStart,
      timestamp_15mPointNumEnd,
      chunkSize
    );

    let targetModelIdTimeHourStrToEonPrediction = {};
    eonPredictorPredictions.forEach((prediction) => {
      const isoString = new Date(parseInt(prediction.timeStamp)).toISOString();
      const isoStringTruncToHour = isoString.slice(0, 13);
      targetModelIdTimeHourStrToEonPrediction[`${prediction.targetModelId}_${isoStringTruncToHour}`] = prediction.predictValue;
    });
    kpis.forEach((kpi) => {
      kpi.targetModelId = generateModelIdFromModelNames(kpi.modelName1, kpi.modelName2, symbol);
      kpi.modelName1 = kpi.modelName1.replace(":", "_");
      kpi.modelName2 = kpi.modelName2.replace(":", "_");
    });

    /**
     * construct multiLine Data
     */
    const modelToCumulativeLine = {}; // 用来存每个model对应的累计收益
    const modelToDiffLine = {}; // 用来存每个model对应的收益
    let lastUsedEonPrediction = {}; // 用来存每个model对应的最后一个eonPrediction
    kpis.forEach((kpi) => {
      const timestampMS = parseInt(kpi.timestamp_15mPointNum.split("_")[0]);
      const pnl_start_time = timestampMS - kpi.fifteenMinPointNum * 15 * 60 * 1000; // timestamp_15mPointNum is when the pnl is generated, so we need to subtract the time it takes to generate the start of the pnl period
      const isoStringTruncToHour = new Date(pnl_start_time).toISOString().slice(0, 13); // 用来匹配eonPredictor的prediction
      const eonPrediction = targetModelIdTimeHourStrToEonPrediction[`${kpi.targetModelId}_${isoStringTruncToHour}`];
      const returnOfThisPoint = typeof kpi[cumulativeReturnCol] === "number" ? kpi[cumulativeReturnCol] : kpi["cumulativeReturn"];

      const effectiveEonPrediction = typeof eonPrediction === "number" ? eonPrediction : lastUsedEonPrediction[kpi.targetModelId]; // 有时候eonPrediction会miss掉几个小时，这时候就用上一个eonPrediction的值
      if (typeof effectiveEonPrediction === "number") {
        lastUsedEonPrediction[kpi.targetModelId] = effectiveEonPrediction;
      }

      modelToCumulativeLine[`${kpi.modelName1}_Enigma`] = modelToCumulativeLine[`${kpi.modelName1}_Enigma`] || [];
      modelToCumulativeLine[`${kpi.modelName1}_Enigma`].push({
        x: timestampMS,
        y:
          modelToCumulativeLine[`${kpi.modelName1}_Enigma`].length > 0
            ? modelToCumulativeLine[`${kpi.modelName1}_Enigma`].slice(-1)[0].y * returnOfThisPoint
            : returnOfThisPoint,
        isDeactivated: kpi.isDeactivated,
      });
      modelToDiffLine[kpi.modelName1] = modelToDiffLine[kpi.modelName1] || [];
      modelToDiffLine[kpi.modelName1].push({
        x: timestampMS,
        y: returnOfThisPoint,
        isDeactivated: kpi.isDeactivated,
      });

      if (typeof effectiveEonPrediction === "number") {
        const returnWithEon = 1 + (returnOfThisPoint - 1) * effectiveEonPrediction;
        // 如果有eon prediction, 则计算加入eon prediction的预测值
        modelToCumulativeLine[`${kpi.modelName1}_EON`] = modelToCumulativeLine[`${kpi.modelName1}_EON`] || [];
        modelToCumulativeLine[`${kpi.modelName1}_EON`].push({
          x: timestampMS,
          y:
            modelToCumulativeLine[`${kpi.modelName1}_EON`].length > 0
              ? modelToCumulativeLine[`${kpi.modelName1}_EON`].slice(-1)[0].y * returnWithEon
              : returnWithEon,
          isDeactivated: kpi.isDeactivated,
        });
        modelToDiffLine[`${kpi.modelName1}_EON`] = modelToDiffLine[`${kpi.modelName1}_EON`] || [];
        modelToDiffLine[`${kpi.modelName1}_EON`].push({
          x: timestampMS,
          y: returnOfThisPoint * returnWithEon,
          isDeactivated: kpi.isDeactivated,
        });
      }
    });

    /**
     * construct averageLine
     */
    let sumPNL = [0]; // 这个时间点active model做的prediction总和
    let sumPNLAll = [0]; // 这个时间点所有model做的prediction总和, including deactivated model
    let predNumber = [0]; // 这个时间点有多少model做了prediction
    let predNumberAll = [0]; // 这个时间点有多少model做了prediction, including deactivated model
    let averageCumulativePNL = [];
    let averageCumulativePNLAll = []; // including deactivated model
    let averagePNL_Enigma = [];
    let averagePNLAll = []; // including deactivated model
    const PRED_INTERVAL_SEC = intervalIn15Mins * 15 * 60;
    for (let i = 0; i < kpis.length; i++) {
      const kpi = kpis[i];
      const nextKpi = i === kpis.length - 1 ? undefined : kpis[i + 1];

      let index = sumPNL.length - 1; // index that corresponds to this model's kpi
      const returnOfThisPoint = typeof kpi[cumulativeReturnCol] === "number" ? kpi[cumulativeReturnCol] : kpi["cumulativeReturn"];
      if (!kpi.isDeactivated) {
        // 如果modal没有被deactivate，则纳入sumPNL，用来计算average
        sumPNL[index] += returnOfThisPoint;
        predNumber[index] += 1;
      }
      sumPNLAll[index] += returnOfThisPoint;
      predNumberAll[index] += 1;

      const isLastPredAtThisTime = !nextKpi || kpi.timestamp + PRED_INTERVAL_SEC / 2 < nextKpi.timestamp;
      if (isLastPredAtThisTime) {
        // 上个时间点的pred统计完了，计算average
        // calculate average PNL of activated models
        const lastPNL = averageCumulativePNL.length > 0 ? averageCumulativePNL[averageCumulativePNL.length - 1].y : 1;
        const timestampMS = parseInt(kpi.timestamp_15mPointNum.split("_")[0]);
        const avergePNL = predNumber[predNumber.length - 1] === 0 ? 1 : sumPNL[sumPNL.length - 1] / predNumber[predNumber.length - 1];
        averageCumulativePNL.push({
          x: timestampMS,
          y: lastPNL * avergePNL,
        });
        averagePNL_Enigma.push({
          x: timestampMS,
          y: avergePNL - 1,
        });

        // calculate average PNL of all models
        const lastPNLAll = averageCumulativePNLAll.length > 0 ? averageCumulativePNLAll[averageCumulativePNLAll.length - 1].y : 1;
        const avergePNLAll =
          predNumberAll[predNumberAll.length - 1] === 0 ? 1 : sumPNLAll[sumPNLAll.length - 1] / predNumberAll[predNumberAll.length - 1];
        averageCumulativePNLAll.push({
          x: timestampMS,
          y: lastPNLAll * avergePNLAll,
        });
        averagePNLAll.push({
          x: timestampMS,
          y: avergePNLAll - 1,
          color: avergePNLAll > 1 ? "rgb(44, 186, 49, 0.85)" : "rgb(255, 87, 34, 0.85)",
        });

        // 创建新的时间点
        sumPNL.push(0);
        predNumber.push(0);
        sumPNLAll.push(0);
        predNumberAll.push(0);
      }
    }

    modelToCumulativeLine["averagePNL_Enigma"] = averageCumulativePNL;
    modelToCumulativeLine["averagePNLAllModels_Enigma"] = averageCumulativePNLAll;
    modelToDiffLine["averagePNL_Enigma"] = averagePNL_Enigma;
    modelToDiffLine["averagePNLAllModels_Enigma"] = averagePNLAll;

    /**
     * construct averageLine with EonPrediction
     */
    sumPNL = [0]; // 这个时间点active model做的prediction总和
    sumPNLAll = [0]; // 这个时间点所有model做的prediction总和, including deactivated model
    predNumber = [0]; // 这个时间点有多少model做了prediction
    predNumberAll = [0]; // 这个时间点有多少model做了prediction, including deactivated model
    averageCumulativePNL = [];
    averageCumulativePNLAll = []; // including deactivated model
    averagePNL_Enigma = [];
    averagePNLAll = []; // including deactivated model
    lastUsedEonPrediction = {}; // 用来存每个model对应的最后一个eonPrediction
    for (let i = 0; i < kpis.length; i++) {
      const kpi = kpis[i];
      const nextKpi = i === kpis.length - 1 ? undefined : kpis[i + 1];

      const timestampMS = parseInt(kpi.timestamp_15mPointNum.split("_")[0]);
      const pnl_start_time = timestampMS - kpi.fifteenMinPointNum * 15 * 60 * 1000; // timestamp_15mPointNum is when the pnl is generated, so we need to subtract the time it takes to generate the start of the pnl period
      const isoStringTruncToHour = new Date(pnl_start_time).toISOString().slice(0, 13); // 用来匹配eonPredictor的prediction
      const eonPrediction = targetModelIdTimeHourStrToEonPrediction[`${kpi.targetModelId}_${isoStringTruncToHour}`];
      const effectiveEonPrediction = typeof eonPrediction === "number" ? eonPrediction : lastUsedEonPrediction[kpi.targetModelId]; // 有时候eonPrediction会miss掉几个小时，这时候就用上一个eonPrediction的值

      if (typeof effectiveEonPrediction !== "number") {
        continue; // 如果没有eonPrediction，则跳过这个kpi
      }
      lastUsedEonPrediction[kpi.targetModelId] = effectiveEonPrediction;

      let index = sumPNL.length - 1; // index that corresponds to this model's kpi
      const returnOfThisPoint = typeof kpi[cumulativeReturnCol] === "number" ? kpi[cumulativeReturnCol] : kpi["cumulativeReturn"];
      const returnWithEon = 1 + (returnOfThisPoint - 1) * effectiveEonPrediction;
      if (!kpi.isDeactivated) {
        // 如果modal没有被deactivate，则纳入sumPNL，用来计算average
        sumPNL[index] += returnWithEon;
        predNumber[index] += 1;
      }
      sumPNLAll[index] += returnWithEon;
      predNumberAll[index] += 1;

      const isLastPredAtThisTime = !nextKpi || kpi.timestamp + PRED_INTERVAL_SEC / 2 < nextKpi.timestamp;
      if (isLastPredAtThisTime) {
        // 上个时间点的pred统计完了，计算average
        // calculate average PNL of activated models
        const lastPNL = averageCumulativePNL.length > 0 ? averageCumulativePNL[averageCumulativePNL.length - 1].y : 1;
        const timestampMS = parseInt(kpi.timestamp_15mPointNum.split("_")[0]);
        const avergePNL = predNumber[predNumber.length - 1] === 0 ? 1 : sumPNL[sumPNL.length - 1] / predNumber[predNumber.length - 1];
        averageCumulativePNL.push({
          x: timestampMS,
          y: lastPNL * avergePNL,
        });
        averagePNL_Enigma.push({
          x: timestampMS,
          y: avergePNL - 1,
        });

        // calculate average PNL of all models
        const lastPNLAll = averageCumulativePNLAll.length > 0 ? averageCumulativePNLAll[averageCumulativePNLAll.length - 1].y : 1;
        const avergePNLAll =
          predNumberAll[predNumberAll.length - 1] === 0 ? 1 : sumPNLAll[sumPNLAll.length - 1] / predNumberAll[predNumberAll.length - 1];
        averageCumulativePNLAll.push({
          x: timestampMS,
          y: lastPNLAll * avergePNLAll,
        });
        averagePNLAll.push({
          x: timestampMS,
          y: avergePNLAll - 1,
          color: avergePNLAll > 1 ? "rgb(44, 186, 49, 0.85)" : "rgb(255, 87, 34, 0.85)",
        });

        // 创建新的时间点
        sumPNL.push(0);
        predNumber.push(0);
        sumPNLAll.push(0);
        predNumberAll.push(0);
      }
    }

    if (averageCumulativePNL.length > 0) {
      // only save averagePNL_EON if there is any data
      modelToCumulativeLine["averagePNL_EON"] = averageCumulativePNL;
      modelToCumulativeLine["averagePNLAllModels_EON"] = averageCumulativePNLAll;
      modelToDiffLine["averagePNL_EON"] = averagePNL_Enigma;
      modelToDiffLine["averagePNLAllModels_EON"] = averagePNLAll;
    }

    const cumulatedPNLList = Object.keys(modelToCumulativeLine)
      .map((modelName1) => {
        const zones = calcZones(modelToCumulativeLine, modelName1);
        return {
          zones,
          zoneAxis: "x",
          type: "spline",
          turboThreshold: 0,
          showInLegend: true,
          visible: pnlVisibility,
          name: modelName1.replace(/.model|.best|.long|size30_/g, "").replace(".epoch", ".ep"),
          data: modelToCumulativeLine[modelName1],
          lineWidth: modelName1.includes("average") ? 4 : 2,
          color: zones[0].color,
        };
      })
      .sort((pnlList1, pnlList2) => {
        const [year1, month1, date1] = pnlList1.name.split(".")[0].split("-");
        const [year2, month2, date2] = pnlList2.name.split(".")[0].split("-");
        return new Date(`${year1}-${month1}-${date1}`) - new Date(`${year2}-${month2}-${date2}`);
      });

    // const pnlList = ["averagePNL_Enigma", "averagePNLAllModels_Enigma"]
    const pnlList = ["averagePNLAllModels_Enigma"]
      .map((modelName1) => {
        return {
          type: "column",
          turboThreshold: 0,
          showInLegend: true,
          visible: pnlVisibility,
          name: modelName1.replace(/.model|.best|.long|size30_/g, "").replace(".epoch", ".ep"),
          data: modelToDiffLine[modelName1],
          lineWidth: modelName1.includes("average") ? 4 : 2,
        };
      })
      .sort((pnlList1, pnlList2) => {
        const [year1, month1, date1] = pnlList1.name.split(".")[0].split("-");
        const [year2, month2, date2] = pnlList2.name.split(".")[0].split("-");
        return new Date(`${year1}-${month1}-${date1}`) - new Date(`${year2}-${month2}-${date2}`);
      });

    return { cumulatedPNLList, pnlList };
  }

  //获取Trading Details 信息
  static async getAndParseTradeAccountStatus(exchange, accountSymbol) {
    const {
      data: { getTradeAccountStatus: accountStatusRaw },
    } = await API.graphql(
      graphqlOperation(queries.getTradeAccountStatus, {
        accountSymbol: accountSymbol,
        exchange: exchange,
      })
    );
    return parseTradeAccountStatus(accountStatusRaw);
  }

  static async getAndParseAccountBalances({ exchangeInstrument, account, startAt, endAt }) {
    const instrument = determineTradeTypeByExchangeInstrument(exchangeInstrument);
    const exchange = exchangeInstrument.split("_")[0];

    // Get account info
    let exchangeAccount, parentAccount;
    if (instrument === INSTRUMENT.cme_future) {
      const {
        data: { getMonitoringAccounts: monitoringAccount },
      } = await API.graphql(
        graphqlOperation(queries.getMonitoringAccounts, {
          exchange: "cme",
          account,
        })
      );
      parentAccount = monitoringAccount.parentAccount || account;
      exchangeAccount = `${exchangeInstrument}_${parentAccount}`;
    } else {
      parentAccount = account;
      exchangeAccount = `${exchangeInstrument}_${account}`;
    }

    // Fetch both account and parent balances in parallel
    const [accountBalances, parentAccountBalances] = await Promise.all([
      fetchAccountBalances(exchangeAccount, startAt - 12 * 3600 * 1000, endAt),
      fetchAccountBalances(`${exchange}_${parentAccount}`, startAt - 12 * 3600 * 1000, endAt),
    ]);

    // Process trade sessions
    const tradeSessionsArray = [];
    accountBalances.forEach((balance) => {
      if (balance.tradeSessions?.length > 0) {
        balance.tradeSessions.forEach((session) => {
          const parsedSession = JSON.parse(session);
          const date = new Date(Number(balance.timestamp));
          const timeStr = `${date.toLocaleDateString()} ${dayjs(date).format("HH:00")}`;
          parsedSession.timestamp = timeStr;
          tradeSessionsArray.push(parsedSession);
        });
      }
    });

    const formatBalances = (parsedBalance, isParentAccount = false) => {
      const regular = {};
      const totals = {};

      // Helper for decimal formatting
      const formatValue = (value, key) => {
        const numValue = typeof value === "object" ? value.total : value;
        if (Number(numValue) === 0) return null;

        if (key.startsWith("totalIn")) {
          return Number(Number(numValue).toFixed(4));
        }
        return Number(Number(numValue).toFixed(2));
      };

      // Process regular values first
      Object.entries(parsedBalance)
        .filter(([key]) => !key.startsWith("totalIn"))
        .forEach(([key, value]) => {
          const formattedValue = formatValue(value, key);
          if (formattedValue !== null) {
            regular[key] = formattedValue;
          }
        });

      // Process totalIn values last
      Object.entries(parsedBalance)
        .filter(([key]) => key.startsWith("totalIn"))
        .forEach(([key, value]) => {
          const formattedValue = formatValue(value, key);
          if (formattedValue !== null) {
            totals[key] = formattedValue;
          }
        });

      return { ...regular, ...totals };
    };

    // Process regular account balances
    let aggregBalances = {};
    for (let i = 0; i < accountBalances.length; i++) {
      const balance = accountBalances[i];
      const date = new Date(Number(balance.timestamp));
      const timeStr = `${date.toLocaleDateString()} ${dayjs(date).format("HH:00")}`;

      if (!aggregBalances[balance.exchangeAccount]) {
        aggregBalances[balance.exchangeAccount] = {
          key: balance.exchangeAccount,
          account: balance.exchangeAccount,
          parentAccount: `${balance.exchangeAccount.split("_")[0]}_${balance.parentAccount}`,
          balancesObj: {},
        };
      }

      const parsedBalance = JSON.parse(balance.balances);
      aggregBalances[balance.exchangeAccount].balancesObj[timeStr] = formatBalances(parsedBalance);
    }

    // Process parent balances
    let parentBalances = {};
    parentAccountBalances
      .filter((balance) => {
        if (exchangeInstrument.includes(INSTRUMENT.coin_future)) {
          return balance.type?.toLowerCase() !== "combined";
        }
        return balance.type?.toLowerCase() === "combined";
      })
      .forEach((balance) => {
        const date = new Date(Number(balance.timestamp));
        const timeStr = `${date.toLocaleDateString()} ${dayjs(date).format("HH:00")}`;

        if (!parentBalances[balance.exchangeAccount]) {
          parentBalances[balance.exchangeAccount] = {
            key: balance.exchangeAccount,
            account: balance.exchangeAccount,
            balancesObj: {},
          };
        }

        const parsedBalance = JSON.parse(balance.balances);
        parentBalances[balance.exchangeAccount].balancesObj[timeStr] = formatBalances(parsedBalance, true);
      });

    return { aggregBalances, parentBalances, tradeSessionsArray };
  }

  /**
   * call graphql query "listManagedTradeAccountStatus" and flat response parsedTradeAccountStatusList to single depth.
   * example:
   *   To get "account" property:
   *     1. retrieve by parsedTradeAccountStatusList.tradeAccountStatus.account
   *     2. retrieve by parsedTradeAccountStatusList.account (the tradeAccountStatus properties are flattened)
   */
  static async listManagedTradeAccountStatus() {
    try {
      const {
        data: { listManagedTradeAccountStatus },
      } = await API.graphql(graphqlOperation(queries.listManagedTradeAccountStatus));

      for (const { parsedTradeAccountStatusList } of listManagedTradeAccountStatus) {
        for (const parsedTradeAccountStatus of parsedTradeAccountStatusList) {
          parsedTradeAccountStatus.parsedConfigs = JSON.parse(parsedTradeAccountStatus.parsedConfigs);
          parsedTradeAccountStatus.parsedModes = JSON.parse(parsedTradeAccountStatus.parsedModes);
          defaults(parsedTradeAccountStatus, parsedTradeAccountStatus.tradeAccountStatus);
        }
      }

      return listManagedTradeAccountStatus;
    } catch (error) {
      message.error(errorToString(error));
    }
  }

  // edit 和 add group都是用这个method
  static async updateGroup(email, groupedAccountsObj) {
    // 变换成数据库接受的格式 {binance: [binance_test1]} -> [{name: binance, includedAccounts: [binance_test1]}]
    const tradeAccountGroupsArray = Object.keys(groupedAccountsObj).reduce((accountArray, group) => {
      return group === UNGROUPED_NAME
        ? accountArray // UNGROUPED 账户不返回
        : accountArray.concat({
            name: group,
            includedAccounts: groupedAccountsObj[group],
          });
    }, []);

    return API.graphql(
      graphqlOperation(mutations.updateCognitoAdminPermission, {
        input: {
          email,
          tradeAccountGroups: tradeAccountGroupsArray,
        },
      })
    );
  }

  static async stopTradeExecution(stopTradeParams) {
    await Service.throwErrorIfUserNotAllowedToControlAccount(stopTradeParams.exchange, stopTradeParams.account);

    try {
      const {
        data: { stopTradeInSession: stopTradeResult },
      } = await API.graphql(
        graphqlOperation(mutations.stopTradeInSession, {
          body: {
            sessionName: stopTradeParams.sessionName,
          },
        })
      );
      return parseTradeGatewayResponse(stopTradeResult);
    } catch (error) {
      return parseTradeGatewayResponse(error);
    }
  }

  static async setLeverage(setLeverageParams) {
    await Service.throwErrorIfUserNotAllowedToControlAccount(setLeverageParams.exchangeInstrument, setLeverageParams.account);

    try {
      const {
        data: { setLeverage: setLeverageResult },
      } = await API.graphql(
        graphqlOperation(mutations.setLeverage, {
          body: {
            exchangeInstrument: setLeverageParams.exchangeInstrument,
            account: setLeverageParams.account,
            pair: setLeverageParams.pair,
            leverage: setLeverageParams.leverage,
            sessionName: setLeverageParams.sessionName,
          },
        })
      );

      return parseTradeGatewayResponse(setLeverageResult);
    } catch (error) {
      return parseTradeGatewayResponse(error);
    }
  }

  static async throwErrorIfUserNotAllowedToControlAccount(exchange, account) {
    const userInfo = await getUserInfo();
    const adminPermission = await Service.getCognitoAdminPermission(userInfo.email);

    const exchangeId = getExchangeIdFromExchangeInstrument(exchange);
    const exchangeAccount = `${exchangeId}_${account}`;

    /**
     * getUserPermission
     */
    let userPermission = adminPermission.managedAccounts.filter((managedAccount) => managedAccount.name === exchangeAccount)[0];

    if (!userPermission || !userPermission.permissions || !userPermission.permissions.includes("CONTROL")) {
      throw new Error("Exchange account does not exist for this user");
    }
  }

  static async startTradeExecution(startTradeParams) {
    await Service.throwErrorIfUserNotAllowedToControlAccount(startTradeParams.exchange, startTradeParams.account);

    try {
      const {
        data: { startTradeInSession: startTradeResult },
      } = await API.graphql(
        graphqlOperation(mutations.startTradeInSession, {
          body: {
            ...startTradeParams,
          },
        })
      );
      return parseTradeGatewayResponse(startTradeResult);
    } catch (error) {
      return parseTradeGatewayResponse(error);
    }
  }

  static async closePositionTradeExecution(closePositionParams) {
    await Service.throwErrorIfUserNotAllowedToControlAccount(closePositionParams.exchange, closePositionParams.account);

    try {
      const {
        data: { closePositionInSession: closePositionResult },
      } = await API.graphql(
        graphqlOperation(mutations.closePositionInSession, {
          body: {
            ...closePositionParams,
          },
        })
      );
      return parseTradeGatewayResponse(closePositionResult);
    } catch (error) {
      return parseTradeGatewayResponse(error);
    }
  }

  static async newTradeExecution(newTradeParams) {
    throw new Error("New trade execution is not supported yet"); // TODO: implement it
  }

  static async fetchEventLogs(tradeAccounts, startAt, endAt = Date.now()) {
    const eventLogsArr = await Promise.all(
      tradeAccounts.map((tradeAccount) => fetchEventLogsForSingleTradeAccount(tradeAccount, startAt, endAt))
    );
    return eventLogsArr.flat();
  }

  static async fetchEventLogsByActions(startAt, endAt, adminUser, actions) {
    let allEventLogs = [];
    for (const action of actions) {
      const eventLogs = await fetchEventLogsByActionType(action, startAt, endAt, adminUser);
      allEventLogs = allEventLogs.concat(eventLogs);
    }
    return allEventLogs;
  }
  /**
   * fetch eon predictor prediction (weight) for a model
   *  */
  static async fetchModelPredictions(modelName, timestamp_start, timestamp_end) {
    let nextToken = undefined;
    let modelWeights = [];

    do {
      const {
        data: { listModelPredictions },
      } = await API.graphql(
        graphqlOperation(queries.listModelPredictions, {
          modelName,
          timeStamp: {
            between: [timestamp_start, timestamp_end],
          },
          sortDirection: "DESC",
          limit: 1000,
          nextToken,
        })
      );
      if (listModelPredictions.items.length > 0) {
        modelWeights = modelWeights.concat(listModelPredictions.items);
      }
      nextToken = listModelPredictions.nextToken;
    } while (nextToken);

    return modelWeights;
  }

  static async fetchParallel(fetchFunction, start, end, chunkSize) {
    const chunks = [];
    for (let chunkStart = start; chunkStart < end; chunkStart += chunkSize) {
      let chunkEnd = Math.min(chunkStart + chunkSize - 1, end);
      if (chunkEnd === end - 1) {
        chunkEnd += 1; // we want the closed range [start, end]
      }
      chunks.push({ start: chunkStart, end: chunkEnd });
    }
    const promises = chunks.map(({ start, end }) => fetchFunction(start, end));
    const batches = await Promise.all(promises);
    return batches.flat();
  }

  static async updateApiKeys({
    exchangeAccount,
    readApiKey,
    readApiSecret,
    tradeApiKey,
    tradeApiSecret,
    readApiPassword,
    tradeApiPassword,
    rsaPublicKey,
  }) {
    try {
      if (!exchangeAccount) {
        throw new Error("Account is missing.");
      }
      if (!rsaPublicKey) {
        throw new Error("Encryption public key is missing. Please contact admins.");
      }
      if (
        (readApiKey && !readApiSecret) ||
        (!readApiKey && readApiSecret) ||
        (tradeApiKey && !tradeApiSecret) ||
        (!tradeApiKey && tradeApiSecret)
      ) {
        throw new Error("API Key or Secret is missing");
      }

      const cryptoKey = await importRsaKey(rsaPublicKey);

      let encryptedReadApiKey,
        encryptedReadApiSecret,
        encryptedTradeApiKey,
        encryptedTradeApiSecret,
        encryptedReadApiPassword,
        encryptedTradeApiPassword;

      if (readApiKey && readApiSecret) {
        encryptedReadApiKey = await encrypt(cryptoKey, readApiKey);
        encryptedReadApiSecret = await encrypt(cryptoKey, readApiSecret);
      }
      if (tradeApiKey && tradeApiSecret) {
        encryptedTradeApiKey = await encrypt(cryptoKey, tradeApiKey);
        encryptedTradeApiSecret = await encrypt(cryptoKey, tradeApiSecret);
      }
      if (readApiPassword) {
        encryptedReadApiPassword = await encrypt(cryptoKey, readApiPassword);
      }
      if (tradeApiPassword) {
        encryptedTradeApiPassword = await encrypt(cryptoKey, tradeApiPassword);
      }

      const {
        data: { updateApiKeys },
      } = await API.graphql(
        graphqlOperation(mutations.updateApiKeys, {
          exchangeAccount,
          readApiKey: encryptedReadApiKey,
          readApiSecret: encryptedReadApiSecret,
          tradeApiKey: encryptedTradeApiKey,
          tradeApiSecret: encryptedTradeApiSecret,
          readApiPassword: encryptedReadApiPassword,
          tradeApiPassword: encryptedTradeApiPassword,
        })
      );
      return updateApiKeys;
    } catch (error) {
      if (error.errors) {
        console.error(error.errors[0].message);
        throw new Error(error.errors[0].message);
      }
      throw error;
    }
  }

  static async fetchRsaPublicKey() {
    const {
      data: { getRsaPublicKey: rsaPublicKey },
    } = await API.graphql(graphqlOperation(queries.getRsaPublicKey));

    return rsaPublicKey;
  }

  static async createTradeAccount({
    exchangeAccount,
    sessionName,
    readApiKey,
    readApiSecret,
    tradeApiKey,
    tradeApiSecret,
    readApiPassword,
    tradeApiPassword,
    rsaPublicKey,
  }) {
    try {
      const cryptoKey = await importRsaKey(rsaPublicKey);
      const encryptedReadApiKey = await encrypt(cryptoKey, readApiKey);
      const encryptedReadApiSecret = await encrypt(cryptoKey, readApiSecret);
      const encryptedTradeApiKey = await encrypt(cryptoKey, tradeApiKey);
      const encryptedTradeApiSecret = await encrypt(cryptoKey, tradeApiSecret);
      const encryptedReadApiPassword = readApiPassword ? await encrypt(cryptoKey, readApiPassword) : undefined;
      const encryptedTradeApiPassword = tradeApiPassword ? await encrypt(cryptoKey, tradeApiPassword) : undefined;

      const {
        data: { createTradeAccount },
      } = await API.graphql(
        graphqlOperation(mutations.createTradeAccount, {
          body: {
            exchangeAccount,
            sessionName,
            readApiKey: encryptedReadApiKey,
            readApiSecret: encryptedReadApiSecret,
            tradeApiKey: encryptedTradeApiKey,
            tradeApiSecret: encryptedTradeApiSecret,
            readApiPassword: encryptedReadApiPassword,
            tradeApiPassword: encryptedTradeApiPassword,
          },
        })
      );
      return parseTradeGatewayResponse(createTradeAccount);
    } catch (error) {
      if (error.errors) {
        return parseTradeGatewayResponse(error);
      }
      throw error;
    }
  }

  static async createTradeSession({ exchangeAccount, parentAccount, sessionName }) {
    try {
      const {
        data: { createTradeAccount },
      } = await API.graphql(
        graphqlOperation(mutations.createTradeAccount, {
          body: {
            exchangeAccount,
            parentAccount,
            sessionName,
          },
        })
      );
      return parseTradeGatewayResponse(createTradeAccount);
    } catch (error) {
      if (error.errors) {
        return parseTradeGatewayResponse(error);
      }
      throw error;
    }
  }
  static async createCoinBasedTradeSession({ exchangeAccount, sessionName }) {
    try {
      const {
        data: { createTradeAccount },
      } = await API.graphql(
        graphqlOperation(mutations.createTradeAccount, {
          body: {
            exchangeAccount,
            sessionName,
          },
        })
      );
      return parseTradeGatewayResponse(createTradeAccount);
    } catch (error) {
      if (error.errors) {
        return parseTradeGatewayResponse(error);
      }
      throw error;
    }
  }

  static async createSpotTradeSession({ exchangeAccount, sessionName }) {
    try {
      const {
        data: { createTradeAccount },
      } = await API.graphql(
        graphqlOperation(mutations.createTradeAccount, {
          body: {
            exchangeAccount,
            sessionName,
          },
        })
      );
      return parseTradeGatewayResponse(createTradeAccount);
    } catch (error) {
      if (error.errors) {
        return parseTradeGatewayResponse(error);
      }
      throw error;
    }
  }

  static async deleteTradeAccount({ exchangeAccount, sessionName }) {
    try {
      const {
        data: { deleteTradeAccount },
      } = await API.graphql(
        graphqlOperation(mutations.deleteTradeAccount, {
          body: {
            exchangeAccount,
            sessionName,
          },
        })
      );
      return parseTradeGatewayResponse(deleteTradeAccount);
    } catch (error) {
      if (error.errors) {
        return parseTradeGatewayResponse(error);
      }
      throw error;
    }
  }

  static async fetchTradeSessionNameSet() {
    const {
      data: {
        listTradeSessions: { response },
      },
    } = await API.graphql(graphqlOperation(queries.listTradeSessions));
    const tradeSessionStr = response;
    /**
     * tradeSessionNameSet 用来筛选 TradeAccountStatus，如果没有出现在
     * tradeSessionNameSet里，则对应的accountStatus不在tradingServer里，因此无法控制
     */
    const tradeSessionNameSet = new Set(
      tradeSessionStr
        .split("\n")
        .map((sessionStr) => sessionStr.split(" ")[0].replace(":", ""))
        .filter((str) => str.length > 0)
    );

    return tradeSessionNameSet;
  }

  static async listTradeModels() {
    const allModels = [];
    let nextToken = undefined;
    do {
      const {
        data: { listTradeModels: tradeModels },
      } = await API.graphql(
        graphqlOperation(queries.listTradeModels, {
          nextToken,
        })
      );
      nextToken = tradeModels.nextToken;
      allModels.push(...tradeModels.items);
    } while (nextToken);

    return allModels;
  }

  static async listWalletBalances(exchangeId, account) {
    const {
      data: { listWalletBalances: balance },
    } = await API.graphql(
      graphqlOperation(queries.listWalletBalances, {
        query: {
          exchangeId,
          account,
        },
      })
    );
    // json string: {
    //  spot: [{asset: "USDT", maxWithdrawAmount: 1000}, {asset: "BUSD", maxWithdrawAmount: 1000}],
    //  future: [{asset: "USDT", maxWithdrawAmount: 1000}, {asset: "BUSD", maxWithdrawAmount: 1000}],
    //  delivery: [{asset: "BTC", maxWithdrawAmount: 1}, {asset: "ETH", maxWithdrawAmount: 1}],
    // }
    return JSON.parse(balance);
  }
  static async transferWalletFund({ exchangeId, account, from, to, code, amount }) {
    try {
      const {
        data: { transferWalletFund: transferMsg },
      } = await API.graphql(
        graphqlOperation(mutations.transferWalletFund, {
          body: {
            exchangeId,
            account,
            from,
            to,
            code,
            amount,
          },
        })
      );
      return JSON.parse(transferMsg).response;
    } catch (error) {
      console.error(error.message);
      throw new Error("An error occurred during fund transfer");
    }
  }

  static async updateTradeAccountStatus({ exchange, accountSymbol, configs, status }) {
    try {
      const input = {
        exchange,
        accountSymbol,
        configs,
        status,
      };

      // Execute the GraphQL mutation
      const { data } = await API.graphql(graphqlOperation(mutations.updateTradeAccountStatus, { input }));

      console.log("Update successful", data);
      return data.updateTradeAccountStatus; // Return the updated account status data
    } catch (error) {
      console.error("Error updating trade account status:", error);
      throw new Error("Failed to update trade account status.");
    }
  }

  static async updateAlgoTradesUrl({ exchange, accountSymbol, algoTrades }) {
    try {
      const input = {
        exchange,
        accountSymbol,
        algoTrades,
      };

      // Execute the GraphQL mutation to update only the algoTrades field
      const { data } = await API.graphql(graphqlOperation(mutations.updateTradeAccountStatus, { input }));

      console.log("AlgoTrades URL update successful", data);
      return data.updateTradeAccountStatus; // Return the updated account status data
    } catch (error) {
      console.error("Error updating algoTrades URL:", error);
      throw new Error("Failed to update algoTrades URL.");
    }
  }

  static async listTouchstoneSubmissions(system) {
    const allSubmissions = [];
    let nextToken = undefined;
    do {
      const options = {
        nextToken,
        filter: { system: { eq: system } },
      };
      const {
        data: { listTouchstoneSubmissions: submissions },
      } = await API.graphql(graphqlOperation(queries.listTouchstoneSubmissions, options));
      nextToken = submissions.nextToken;
      allSubmissions.push(...submissions.items);
    } while (nextToken);

    return allSubmissions;
  }

  static async listTouchstoneSubmissionsPaginated({ limit = 100, nextToken = null, sortDirection = "DESC" } = {}) {
    const {
      data: { listTouchstoneSubmissionsByTsCreated: submissions },
    } = await API.graphql(
      graphqlOperation(queries.listTouchstoneSubmissionsByTsCreated, {
        gsi_pk: GSI_PK,
        limit,
        nextToken,
        sortDirection,
      })
    );

    return {
      items: submissions.items,
      nextToken: submissions.nextToken,
    };
  }

  static async listTouchstoneTasks() {
    const allTasks = [];
    let nextToken = undefined;
    do {
      const {
        data: { listTouchstoneTasks: tasks },
      } = await API.graphql(graphqlOperation(queries.listTouchstoneTasks, { nextToken }));
      nextToken = tasks.nextToken;
      allTasks.push(...tasks.items);
    } while (nextToken);

    return allTasks;
  }

  static async listTouchstoneTasksPaginated({ limit = 100, nextToken = null, sortDirection = "DESC" } = {}) {
    const {
      data: { listTouchstoneTasksByTimestamp: tasks },
    } = await API.graphql(
      graphqlOperation(queries.listTouchstoneTasksByTimestamp, {
        gsi_pk: GSI_PK,
        limit,
        nextToken,
        sortDirection,
      })
    );

    return {
      items: tasks.items,
      nextToken: tasks.nextToken,
    };
  }

  static async listActiveTouchstoneTasks() {
    const allTasks = [];
    await Promise.all(
      TOUCHSTONE_TASKS_ACTIVE_STATUS.map(async (status) => {
        let nextToken = undefined;
        do {
          const {
            data: { listTouchstoneTasksByStatus: tasks },
          } = await API.graphql(
            graphqlOperation(queries.listTouchstoneTasksByStatus, {
              status,
              nextToken,
            })
          );
          nextToken = tasks.nextToken;
          allTasks.push(...tasks.items);
        } while (nextToken);
      })
    );

    return allTasks;
  }

  static async listTasksForSubmission(submission_id) {
    const allTasks = [];
    let nextToken = undefined;
    try {
      do {
        const {
          data: { listTouchstoneTasks: tasks },
        } = await API.graphql(
          graphqlOperation(queries.listTouchstoneTasks, {
            submission_id: submission_id,
            nextToken,
          })
        );
        nextToken = tasks.nextToken;
        allTasks.push(...tasks.items);
      } while (nextToken);
      return allTasks;
    } catch (error) {
      message.error(`Failed to list tasks for submission: ${error.message}`);
      return [];
    }
  }

  static async listSystemConfigs() {
    const allConfigs = [];
    let nextToken = undefined;
    do {
      const {
        data: { listSystemConfigs: configs },
      } = await API.graphql(
        graphqlOperation(queries.listSystemConfigs, {
          nextToken,
        })
      );
      nextToken = configs.nextToken;
      allConfigs.push(...configs.items);
    } while (nextToken);

    return allConfigs;
  }
}

async function fetchAccountBalances(exchangeAccount, startAt, endAt = Date.now()) {
  let accountBalances = [];
  let nextToken = undefined;
  do {
    const {
      data: { listExchangeAccountBalances: accountBalance },
    } = await API.graphql(
      graphqlOperation(queries.listExchangeAccountBalances, {
        exchangeAccount,
        timestamp: { between: [startAt, endAt] },
        nextToken,
      })
    );
    nextToken = accountBalance.nextToken;
    accountBalances = accountBalances.concat(accountBalance.items);
  } while (nextToken);

  return accountBalances;
}

function parseTradeGatewayResponse(tradeGatewayResult) {
  console.log({ tradeGatewayResult });
  if (tradeGatewayResult.errors) {
    console.error(tradeGatewayResult.errors[0].message);
    if (tradeGatewayResult.errors[0].message.includes("Command failed: tmux send-keys")) {
      throw new Error("Something is wrong, possible reason: sessionName mismatch");
    }
    if (tradeGatewayResult.errors[0].message.includes("Connection refused:")) {
      throw new Error("Something is wrong, possible reason: trade gateway temporarily closed");
    }
    throw new Error(tradeGatewayResult.errors[0].message);
  }

  // if (tradeGatewayResult.response && tradeGatewayResult.response.includes("success")) {
  // }

  return tradeGatewayResult.response;
}

function parseTradeAccountStatus(accountStatus) {
  const { exchange: exchangeInstrument, accountSymbol } = accountStatus;

  const account = accountSymbol
    .split("_")
    .splice(0, accountSymbol.split("_").length - 1)
    .join("_"); // bvi_sub1_BTC/USD ->  // bvi_sub1
  let symbol = accountSymbol.split("_").splice(-1)[0];
  // if (accountStatus.type === "USDT_FUTURE") {
  //   symbol = symbol.includes("BUSD") ? symbol : `${symbol}T`;
  // }
  const exchange = getExchangeIdFromExchangeInstrument(exchangeInstrument); // binance_usds_future -> binance
  const exchangeAccount = `${exchange}_${account}`;

  const tradeConfig = accountStatus && accountStatus.configs && JSON.parse(JSON.parse(accountStatus.configs));
  const tradeModes = accountStatus && accountStatus.modes && JSON.parse(JSON.parse(accountStatus.modes));

  return {
    ...accountStatus,
    account: account,
    symbol: symbol,
    symbolShort: symbol.split("/")[0], // BTC/USDT -> BTC
    exchangeId: exchange,
    exchangeAccount: exchangeAccount,
    key: `${accountStatus.exchange}_${accountStatus.accountSymbol.replace("/", "_")}`,
    parsedConfigs: tradeConfig,
    parsedModes: tradeModes,
  };
}

/**
 * zones用来控制每条线的style，允许我们修改颜色，粗度，在某一段是否虚线等等
 * @param {*} modelToLine
 * @param {*} modelName
 * @returns
 */
function calcZones(modelToLine, modelName) {
  const LINE_STYLE = {
    dash: "dash",
    solid: "solid",
  };

  if (modelName === "averagePNL_Enigma") {
    return [{ color: "#333" }];
  }
  if (modelName === "averagePNLAllModels_Enigma") {
    return [{ color: "#333", dashStyle: LINE_STYLE.dash }];
  }
  if (modelName === "averagePNL_EON") {
    return [{ color: "#607d8b" }];
  }
  if (modelName === "averagePNLAllModels_EON") {
    return [{ color: "#607d8b", dashStyle: LINE_STYLE.dash }];
  }

  const intervalInMin = retrieveIntervalInMinFromModelName(modelName);
  const color = calcLineColorFromIntervalAndModelName(intervalInMin, modelName);
  const zones = [];

  for (let i = 1; i < modelToLine[modelName].length; i++) {
    const isChangePoint = modelToLine[modelName][i - 1].isDeactivated !== modelToLine[modelName][i].isDeactivated;
    const styleShouldBeDashedBeforeChangePoint = modelToLine[modelName][i - 1].isDeactivated;
    if (isChangePoint) {
      zones.push({
        color,
        value: modelToLine[modelName][i].x,
        dashStyle: styleShouldBeDashedBeforeChangePoint ? LINE_STYLE.dash : LINE_STYLE.solid,
      });
    }
  }
  const lastPoint = modelToLine[modelName][modelToLine[modelName].length - 1];
  if (lastPoint.isDeactivated) {
    zones.push({ color, value: lastPoint.x, dashStyle: LINE_STYLE.dash });
  }
  zones.push({ dashStyle: LINE_STYLE.solid, color });
  return zones;
}

async function fetchEventLogsForSingleTradeAccount(tradeAccount, startAt, endAt = Date.now()) {
  let eventLogs = [];
  let nextToken = undefined;
  do {
    const {
      data: { listELAdminEventLogsByTradeAccount: eventLog },
    } = await API.graphql(
      graphqlOperation(queries.listELAdminEventLogsByTradeAccount, {
        tradeAccount,
        timestamp: { between: [startAt, endAt] },
        nextToken,
      })
    );
    nextToken = eventLog.nextToken;
    eventLogs = eventLogs.concat(eventLog.items);
  } while (nextToken);

  return eventLogs;
}

async function fetchEventLogsByActionType(action, startAt, endAt, adminUser) {
  let eventLogs = [];
  let nextToken = undefined;

  do {
    try {
      const response = await API.graphql(
        graphqlOperation(queries.listELAdminEventLogs, {
          filter: {
            and: [{ action: { eq: action } }],
          },
          adminUser: adminUser,
          timestamp: { between: [startAt, endAt] },
          limit: 100,
          nextToken,
          sortDirection: "DESC",
        })
      );
      const { items, nextToken: newToken } = response.data.listELAdminEventLogs;
      eventLogs = eventLogs.concat(items);
      nextToken = newToken;
    } catch (error) {
      console.error(`Error fetching ${action} events:`, error);
      break;
    }
  } while (nextToken);

  return eventLogs;
}
